/**
 * 成因推断卡片组件
 * 展示诱因推断和环境因素
 * Requirements: 3.1, 3.2, 3.3
 */

import React from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Lightbulb, Thermometer, Droplets, AlertCircle } from "lucide-react";
import type { RootCauseCardProps } from "@/types/conversion-funnel";

/**
 * 成因推断卡片
 * 展示 AI 推断的诱因，结合用户鱼缸信息和环境因素
 */
export function RootCauseCard({
  inference,
  tankInfo,
  className,
}: RootCauseCardProps) {
  const { t } = useTranslation("assessments");

  return (
    <div
      className={cn(
        "rounded-xl border bg-white p-4 shadow-sm",
        "flex flex-col h-full",
        "bg-blue-50",
        className
      )}
    >
      {/* 卡片标题 */}
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="h-5 w-5 text-blue-500" />
        <h3 className="text-sm font-semibold text-gray-700">
          {t("conversion.rootCause.title", "成因推断")}
        </h3>
      </div>

      {/* 主要推断文案 */}
      <div className="flex-1 mb-3">
        <p className="text-sm text-gray-700 leading-relaxed">{inference}</p>
      </div>

      {/* 鱼缸信息关联 */}
      {tankInfo && (
        <div className="mb-3 p-3 bg-white rounded-lg border border-blue-100">
          <h4 className="text-xs font-medium text-gray-500 mb-2">
            {t("conversion.rootCause.tankContext", "鱼缸环境")}
          </h4>
          <div className="space-y-2">
            {/* 鱼缸名称 */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Droplets className="h-4 w-4 text-blue-400" />
              <span>{tankInfo.name}</span>
              <span className="text-gray-400">·</span>
              <span>{tankInfo.volume}L</span>
            </div>

            {/* 加热棒状态 */}
            <div className="flex items-center gap-2 text-sm">
              <Thermometer
                className={cn(
                  "h-4 w-4",
                  tankInfo.hasHeater ? "text-green-500" : "text-amber-500"
                )}
              />
              <span
                className={cn(
                  tankInfo.hasHeater ? "text-green-600" : "text-amber-600"
                )}
              >
                {tankInfo.hasHeater
                  ? t("conversion.rootCause.hasHeater", "已配备加热棒")
                  : t("conversion.rootCause.noHeater", "未配备加热棒")}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 环境因素提示 */}
      <EnvironmentFactors />

      {/* 专家分析提示 */}
      <div className="mt-3 pt-3 border-t border-blue-100">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-500">
            {t(
              "conversion.rootCause.expertNote",
              "以上推断基于 AI 分析和专业知识库，结合您的鱼缸环境生成"
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * 环境因素组件
 * 显示季节性和环境相关的提示
 */
function EnvironmentFactors() {
  const { t } = useTranslation("assessments");
  const month = new Date().getMonth() + 1;

  // 根据季节显示不同的环境因素
  const getSeasonalFactor = () => {
    if ([11, 12, 1, 2].includes(month)) {
      return {
        icon: <Thermometer className="h-4 w-4 text-blue-400" />,
        text: t("conversion.rootCause.winterFactor", "冬季气温骤降，注意保温"),
      };
    }
    if ([6, 7, 8].includes(month)) {
      return {
        icon: <Thermometer className="h-4 w-4 text-red-400" />,
        text: t(
          "conversion.rootCause.summerFactor",
          "夏季高温，注意降温和增氧"
        ),
      };
    }
    if ([3, 4, 5].includes(month)) {
      return {
        icon: <Droplets className="h-4 w-4 text-green-400" />,
        text: t("conversion.rootCause.springFactor", "春季换季，注意水质稳定"),
      };
    }
    return {
      icon: <Droplets className="h-4 w-4 text-amber-400" />,
      text: t("conversion.rootCause.autumnFactor", "秋季温差大，注意温度变化"),
    };
  };

  const seasonalFactor = getSeasonalFactor();

  return (
    <div className="p-2 bg-white/50 rounded-lg">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        {seasonalFactor.icon}
        <span>{seasonalFactor.text}</span>
      </div>
    </div>
  );
}

export default RootCauseCard;
